/**
 * Owns the media client for the life of a CALL: create → connect → disconnect.
 *
 * Deliberately the same `MediaClient` the meeting room uses — one LiveKit
 * implementation, one place where publishing, denoise, effects and the
 * subscription plan live. A call is simply a room with 2..n publishers.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CallCredentials } from '@cyphex/types/calls';
import type { MediaClient, MediaSnapshot } from '@/features/meeting/media/contract';
import { createMediaClient } from '@/features/meeting/media/createMediaClient';
import type { MediaPreferences } from '@/features/meeting/media/settings/preferences';
import { registerCallMedia } from '../media/callMediaRegistry';

export interface CallSessionInput {
  /** Which call this session is the media for. See `callMediaRegistry`. */
  readonly callId: string;
  readonly credentials: CallCredentials;
  readonly camera: boolean;
  readonly mic: boolean;
  readonly preferences: MediaPreferences;
}

export interface CallSession {
  readonly client: MediaClient;
  readonly snapshot: MediaSnapshot;
}

export function useCallSession(input: CallSessionInput): CallSession {
  /* Captured once: the grant is single-use, and a re-connect on a preference
     change would tear down a live call to apply a checkbox. */
  const initial = useRef(input);
  const [client] = useState<MediaClient>(() =>
    createMediaClient({
      localId: initial.current.credentials.identity,
      participants: [],
      profile: initial.current.credentials.policy.profile,
      recording: 'off',
      recordingStartedBy: null,
    }),
  );

  const snapshot = useSyncExternalStore(client.subscribe, client.getSnapshot, client.getSnapshot);

  /*
   * THE ONE THING THAT CAN STOP THIS MICROPHONE, ADDRESSABLE BY CALL ID.
   *
   * Unmounting the room already disconnects, below — but only a render can
   * cause an unmount, and an ending that never reaches this document produces
   * no render. Registering the client means `releaseCallMedia(callId)` can be
   * called by whoever finds out, whether or not the tree agrees yet.
   *
   * Declared BEFORE the connect effect on purpose: React runs destroys in
   * declaration order, so on unmount this deregisters first and the disconnect
   * below is left to do the leaving exactly once.
   */
  useEffect(() => registerCallMedia(input.callId, client), [input.callId, client]);

  useEffect(() => {
    const options = initial.current;
    const devices = options.preferences.devices;
    void client.connect({
      url: options.credentials.url,
      token: options.credentials.token,
      roomName: options.credentials.room,
      profile: options.credentials.policy.profile,
      cameraEnabled: options.camera,
      micEnabled: options.mic,
      ...(devices.cameraId ? { cameraDeviceId: devices.cameraId } : {}),
      ...(devices.micId ? { micDeviceId: devices.micId } : {}),
      ...(devices.speakerId ? { speakerDeviceId: devices.speakerId } : {}),
      videoEffect: options.preferences.video,
      audioProcessing: options.preferences.audio,
    });
    return () => {
      void client.disconnect();
    };
  }, [client]);

  return { client, snapshot };
}
